import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Checkbox,
  Container,
  Popover,
  Group,
  Modal,
  Stack,
  Text,
  Title,
  Skeleton,
} from '@mantine/core';
import {
  IconArrowLeft,
  IconPlus,
  IconPencil,
  IconPlayerPlay,
  IconQuestionMark,
} from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { useShallow } from 'zustand/react/shallow';
import { ChatLayout } from '../../components/layout/ChatLayout';
import { AnnouncementModal } from '../../features/chat/components/AnnouncementModal';
import { ProfileModal } from '../../features/chat/components/ProfileModal';
import { useChatStore } from '../../stores/chatStore';
import { trackComponentInteraction } from '../../utils/analytics';
import { formatNzDateRange } from '../../utils/announcementTime';
import { showNotification } from '../../components/Notification';
import { useAnnouncementsQuery, useDeleteAnnouncement } from '../../api';
import styles from './AnnouncementsPage.module.css';

const formatDateRange = (startIso: string, endIso: string): string =>
  formatNzDateRange(startIso, endIso);

const AnnouncementsSkeleton: React.FC = () => (
  <Stack gap="sm" className={styles.selectionSection}>
    <div className={styles.selectAllRow}>
      <Group gap="sm" align="center">
        <Skeleton height={20} width={20} radius="sm" />
        <Skeleton height={18} width={96} radius="sm" />
      </Group>
      <Group gap="md" className={styles.selectionActions}>
        <Skeleton height={28} width={70} radius="sm" />
        <Skeleton height={28} width={110} radius="sm" />
      </Group>
    </div>
    <Stack gap="sm" className={styles.listStack}>
      {Array.from({ length: 4 }).map((_, index) => (
        <div key={index} className={styles.announcementRow}>
          <Group gap="sm" className={styles.announcementMain}>
            <Skeleton height={20} width={20} radius="sm" />
            <Skeleton height={18} width="60%" radius="sm" />
          </Group>
          <Group gap="md" className={styles.announcementMeta}>
            <Skeleton height={20} width={70} radius="sm" />
            <Skeleton height={18} width={120} radius="sm" />
            <Skeleton height={28} width={30} radius="sm" />
            <Skeleton height={28} width={30} radius="sm" />
          </Group>
        </div>
      ))}
    </Stack>
  </Stack>
);

export const AnnouncementsPage: React.FC = () => {
  const navigate = useNavigate();
  const [previewAnnouncementId, setPreviewAnnouncementId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const {
    data: announcements = [],
    isLoading,
    isFetching,
    error: announcementsError,
  } = useAnnouncementsQuery();

  const deleteAnnouncementMutation = useDeleteAnnouncement();

  const {
    isProfileModalOpen,
    toggleProfileModal,
    closeProfileModal,
  } = useChatStore(
    useShallow((state) => ({
      isProfileModalOpen: state.isProfileModalOpen,
      toggleProfileModal: state.toggleProfileModal,
      closeProfileModal: state.closeProfileModal,
    })),
  );

  const [isErrorVisible, setIsErrorVisible] = useState(false);
  const announcementsErrorMessage = useMemo(() => {
    if (!announcementsError) {
      return null;
    }
    if (announcementsError instanceof Error) {
      return announcementsError.message;
    }
    return 'Failed to fetch announcements';
  }, [announcementsError]);

  useEffect(() => {
    if (!announcementsErrorMessage) {
      setIsErrorVisible(false);
      return undefined;
    }

    setIsErrorVisible(true);
    const timeout = setTimeout(() => setIsErrorVisible(false), 4000);
    return () => clearTimeout(timeout);
  }, [announcementsErrorMessage]);

  const handleBack = useCallback(() => {
    trackComponentInteraction(
      'AnnouncementsPage',
      'Back to Admin',
      'button',
      'click',
      'back_to_admin'
    );
    navigate({ to: '/admin' });
  }, [navigate]);

  const handleProfileToggle = useCallback(() => {
    trackComponentInteraction('Header', 'Profile', 'button', 'click', 'profile_access');
    toggleProfileModal();
  }, [toggleProfileModal]);

  const handleCreate = useCallback(() => {
    trackComponentInteraction(
      'AnnouncementsPage',
      'Create Announcement',
      'button',
      'click',
      'go_to_create'
    );
    navigate({
      to: '/create-announcements',
      search: { announcementId: undefined },
    });
  }, [navigate]);

  const handleEdit = useCallback(
    (announcementId: string) => {
      trackComponentInteraction(
        'AnnouncementsPage',
        'Edit Announcement',
        'button',
        'click',
        announcementId
      );
      navigate({
        to: '/create-announcements',
        search: { announcementId },
      });
    },
    [navigate],
  );

  const openPreview = useCallback((announcementId: string) => {
    trackComponentInteraction(
      'AnnouncementsPage',
      'Preview Announcement',
      'button',
      'click',
      announcementId
    );
    setPreviewAnnouncementId(announcementId);
  }, []);

  const closePreview = useCallback(() => {
    if (previewAnnouncementId) {
      trackComponentInteraction(
        'AnnouncementsPage',
        'Preview Announcement',
        'modal',
        'dismiss',
        previewAnnouncementId
      );
    }
    setPreviewAnnouncementId(null);
  }, [previewAnnouncementId]);

  const previewAnnouncement = useMemo(
    () => announcements.find((item) => item.announcementID === previewAnnouncementId),
    [announcements, previewAnnouncementId],
  );

  useEffect(() => {
    setSelectedIds((current) =>
      current.filter((id) => announcements.some((item) => item.announcementID === id)),
    );
  }, [announcements]);

  const decoratedAnnouncements = useMemo(
    () =>
      announcements.map((announcement) => {
        return {
          ...announcement,
          rangeLabel: formatDateRange(announcement.startDate, announcement.endDate),
          isActive: announcement.active,
        };
      }),
    [announcements],
  );

  const showEmpty = !isLoading && decoratedAnnouncements.length === 0;

  const handleToggleSelect = useCallback((announcementId: string) => {
    const isSelected = selectedIds.includes(announcementId);
    trackComponentInteraction(
      'AnnouncementsPage',
      'Select Announcement',
      'checkbox',
      isSelected ? 'deselect' : 'select',
      announcementId
    );
    setSelectedIds((current) =>
      current.includes(announcementId)
        ? current.filter((id) => id !== announcementId)
        : [...current, announcementId],
    );
  }, [selectedIds]);

  const handleSelectAllChange = useCallback(
    (checked: boolean) => {
      trackComponentInteraction(
        'AnnouncementsPage',
        'Select All',
        'checkbox',
        checked ? 'select_all' : 'deselect_all',
        String(decoratedAnnouncements.length)
      );
      if (checked) {
        setSelectedIds(decoratedAnnouncements.map((item) => item.announcementID));
      } else {
        setSelectedIds([]);
      }
    },
    [decoratedAnnouncements],
  );

  const allSelected =
    decoratedAnnouncements.length > 0 &&
    selectedIds.length === decoratedAnnouncements.length;
  const isIndeterminate =
    selectedIds.length > 0 && selectedIds.length < decoratedAnnouncements.length;

  const handleCancelSelection = useCallback(() => {
    if (selectedIds.length > 0) {
      trackComponentInteraction(
        'AnnouncementsPage',
        'Bulk Selection',
        'button',
        'cancel_selection',
        selectedIds.join(',')
      );
    }
    setSelectedIds([]);
  }, [selectedIds]);

  const handleDeleteSelected = useCallback(async () => {
    if (selectedIds.length === 0) {
      return;
    }
    setIsBulkDeleting(true);
    try {
      for (const id of selectedIds) {
        // eslint-disable-next-line no-await-in-loop
        await deleteAnnouncementMutation.mutateAsync(id);
      }
      showNotification('Announcement deleted successfully');
      trackComponentInteraction(
        'AnnouncementsPage',
        'Bulk Delete',
        'button',
        'delete_success',
        selectedIds.join(',')
      );
      setSelectedIds([]);
    } catch (mutationError) {
      console.error('Failed to delete announcements', mutationError);
      showNotification('Failed to delete announcements. Please try again.');
    } finally {
      setIsBulkDeleting(false);
    }
  }, [deleteAnnouncementMutation, selectedIds]);

  const disableSelectionControls =
    isLoading || isFetching || isBulkDeleting || deleteAnnouncementMutation.isPending;

  return (
    <ChatLayout
      showHeaderLeft={false}
      headerAction={
        <div className={styles.headerAction}>
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={22} />}
            className={styles.backButton}
            onClick={handleBack}
          >
            Back
          </Button>
          <Popover
            width={240}
            position="bottom-end"
            offset={0}
            shadow="0px 0px 12px 0px rgba(0, 0, 0, 0.24)"
            opened={isProfileModalOpen}
            onChange={(opened) => {
              if (!opened) {
                closeProfileModal();
              }
            }}
            onClose={closeProfileModal}
            styles={{
              dropdown: {
                border: '1px solid #59B836',
                borderRadius: '6px',
                backgroundColor: '#FFFFFF',
                padding: 0,
              },
            }}
          >
            <Popover.Target>
              <ActionIcon
                size={42}
                variant="filled"
                className={styles.profileButton}
                onClick={handleProfileToggle}
                aria-label="Open profile menu"
              >
                <IconQuestionMark size={24} />
              </ActionIcon>
            </Popover.Target>
            <Popover.Dropdown>
              <ProfileModal onClose={closeProfileModal} />
            </Popover.Dropdown>
          </Popover>
        </div>
      }
    >
      <Container size="xl" className={styles.container}>
        <div className={styles.page}>
          <div className={styles.headingRow}>
            <Title order={1} className={styles.title}>
              Announcements
            </Title>
            <Button
              leftSection={<IconPlus size={20} />}
              className={styles.newButton}
              onClick={handleCreate}
            >
              New
            </Button>
          </div>

          <Stack gap="lg" className={styles.contentStack}>
            {isErrorVisible && announcementsErrorMessage && (
              <Alert
                color="red"
                variant="light"
                withCloseButton
                onClose={() => setIsErrorVisible(false)}
                className={styles.errorAlert}
              >
                {announcementsErrorMessage}
              </Alert>
            )}

            {decoratedAnnouncements.length > 0 && (
              <Stack gap="sm" className={styles.selectionSection}>
                <div className={styles.selectAllRow}>
                  <Checkbox
                    checked={allSelected}
                    indeterminate={isIndeterminate}
                    disabled={decoratedAnnouncements.length === 0 || disableSelectionControls}
                    onChange={(event) => handleSelectAllChange(event.currentTarget.checked)}
                    classNames={{
                      root: styles.checkboxRoot,
                      input: styles.checkboxInput,
                      label: styles.selectAllLabel,
                      body: styles.selectAllBody,
                    }}
                    label="Select All"
                  />
                  {selectedIds.length > 0 && (
                    <Group gap="md" className={styles.selectionActions}>
                      <button
                        type="button"
                        className={styles.cancelAction}
                        onClick={handleCancelSelection}
                        disabled={disableSelectionControls}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className={styles.deleteAction}
                        onClick={handleDeleteSelected}
                        disabled={disableSelectionControls}
                      >
                        Delete Selected
                      </button>
                    </Group>
                  )}
                </div>

                {decoratedAnnouncements.length > 0 && (
                  <Stack gap="sm" className={styles.listStack}>
                    {decoratedAnnouncements.map((announcement) => (
                      <div
                        key={announcement.announcementID}
                        className={styles.announcementRow}
                      >
                        <Group gap="sm" className={styles.announcementMain}>
                          <Checkbox
                            checked={selectedIds.includes(announcement.announcementID)}
                            disabled={disableSelectionControls}
                            onChange={() => handleToggleSelect(announcement.announcementID)}
                            classNames={{
                              root: styles.checkboxRoot,
                              input: styles.checkboxInput,
                            }}
                          />
                          <Text className={styles.itemTitle}>{announcement.title}</Text>
                        </Group>

                        <Group gap="md" className={styles.announcementMeta}>
                          <Badge
                            size="sm"
                            variant="light"
                            radius="sm"
                            className={`${styles.statusBadge} ${
                              announcement.isActive
                                ? styles.statusBadgeActive
                                : styles.statusBadgeExpired
                            }`}
                          >
                            {announcement.isActive ? 'Active' : 'Expired'}
                          </Badge>
                          <Text className={styles.itemDate}>
                            {announcement.rangeLabel}
                          </Text>
                          <ActionIcon
                            className={styles.actionIcon}
                            variant="subtle"
                            radius="sm"
                            aria-label={`Edit announcement ${announcement.title}`}
                            onClick={() => handleEdit(announcement.announcementID)}
                            disabled={disableSelectionControls}
                          >
                            <IconPencil size={22} />
                          </ActionIcon>
                          <ActionIcon
                            className={styles.actionIcon}
                            variant="subtle"
                            radius="sm"
                            aria-label={`Preview announcement ${announcement.title}`}
                            onClick={() => openPreview(announcement.announcementID)}
                            disabled={disableSelectionControls}
                          >
                            <IconPlayerPlay size={22} />
                          </ActionIcon>
                        </Group>
                      </div>
                    ))}
                  </Stack>
                )}
              </Stack>
            )}

            {isLoading && decoratedAnnouncements.length === 0 && (
              <AnnouncementsSkeleton />
            )}

            {showEmpty && (
              <Text className={`${styles.helperText} ${styles.emptyStateText}`}>
                No announcements yet. Use the New button to create the first one.
              </Text>
            )}
          </Stack>
        </div>
      </Container>
      <Modal
        opened={Boolean(previewAnnouncement)}
        onClose={closePreview}
        size="auto"
        centered
        withCloseButton={false}
        padding="sm"
        styles={{
          content: {
            backgroundColor: 'transparent',
          },
          body: {
            padding: 0,
          },
        }}
      >
        {previewAnnouncement && (
          <AnnouncementModal
            announcement={previewAnnouncement}
            onClose={closePreview}
          />
        )}
      </Modal>
    </ChatLayout>
  );
};
